import { memo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Image from 'components/Common/Image'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'
import type { FlightsClassTypes } from 'constants/flight'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import OfferPageReviewAiSummary from 'components/OfferPage/NavigableOfferContent/OfferPageReviewAiSummary'
import useOfferPageReviewSummary from 'hooks/Reviews/useOfferPageReviewSummary'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'
import LoyaltyStayUpgradeBenefitPill from 'loyalty/components/LoyaltyStayUpgradeBenefitPill'
import FlashTierProgressionPanel from 'loyalty/components/Banners/FlashTierProgressionPanel'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import Heading from 'LuxKit/Typography/Heading'
import { useIntl } from 'react-intl'

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.tablet} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

interface Props {
  offer: App.HotelOffer
  property: App.OfferProperty
  suppressTakeoverCampaignTagWhenUSPVisible?: boolean
  charterBundleRoute?: App.CharterBundleRoute
  freeStayLabel?: string
  fareClass?: FlightsClassTypes
  packageViews?: Array<App.OfferPackageOptionView>
  checkoutDate?: string
  checkIn?: string
}

function FlashHotelPropertyDetails(props: Props) {
  const {
    offer,
    property,
    suppressTakeoverCampaignTagWhenUSPVisible = false,
    charterBundleRoute,
    freeStayLabel,
    fareClass,
    packageViews,
    checkoutDate,
    checkIn,
  } = props

  const intl = useIntl()
  const { shouldShowKidsFlyFreeForOffer } = useKidsFlyFree({ charterBundleRoute, fareClass })
  const { isAnyVariant: isHighlightReviewEnabled } = useExperiment(Experiments.croHighlightReviewAiSummary)
  const [reviewSummary, fetchingReviewSummary] = useOfferPageReviewSummary(
    property.id,
    'luxuryescapes',
    isHighlightReviewEnabled,
  )
  const hasReviewSummaryContent = !!reviewSummary?.summary
  const showReviewSummary = isHighlightReviewEnabled && isOfferRatingDisplayable(property.rating)
  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
  const loyaltyRoomUpgradeBenefitDetails = useLoyaltyUserBenefitView('hotel_upgrade')
  const hasUpgradablePackages = offer.loyalty.hasPackageTypeUpgrades || offer.loyalty.hasRoomTypeUpgrades

  const propertyNameWithFlights = charterBundleRoute
    ? intl.formatMessage(
        {
          id: 'h7QcmO8fvNLHQYmsH98L',
          defaultMessage: '{propertyName} & Flights',
          description: 'Property name with flights suffix for charter bundle offers',
        },
        {
          propertyName: offer.property.name || intl.formatMessage({
            id: '1K3EXrzCdqY8du2zz77A',
            defaultMessage: 'Vendor',
            description: 'Fallback text for vendor when property name is not available',
          }),
        },
      )
    : offer.property.name

  return (
    <Group direction="vertical" gap={8} tabletGap={20}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Group direction="vertical" gap={8}>
          <BodyTextBlock data-testid="offer-location" variant="small" startIcon={<LineMapMarkerIcon />}>
            {location}
          </BodyTextBlock>
          <Heading variant="heading2" as="h1">{propertyNameWithFlights}</Heading>
          <CSSBreakpoint max="mobile">
            <Group direction="vertical" gap={8}>
              {shouldShowKidsFlyFreeForOffer(offer, checkIn) && <KidsFlyFreeLabel />}
              {!(showReviewSummary && hasReviewSummaryContent) && isOfferRatingDisplayable(property.rating) && <OfferRating
                link
                inlineLabel
                variant="xs"
                rating={property.rating}
              />}
              <OfferPageLabelGroup
                offer={offer}
                showBadge
                suppressTakeoverCampaignTagWhenUSPVisible={suppressTakeoverCampaignTagWhenUSPVisible}
                freeStayLabel={freeStayLabel}
              />
            </Group>
          </CSSBreakpoint>
          <BodyText variant="large">{offer.name}</BodyText>
        </Group>
        {property.logoImageId &&
          <PropertyLogo
            dpr={2}
            height={75}
            enhancedFormats={false}
            id={property.logoImageId}
            alt={intl.formatMessage(
              {
                id: 'OAyYxWPMQaAP31G2VZFo',
                defaultMessage: '{propertyName} logo',
                description: 'Accessibility alt text for property logo image on the offer detail page',
              },
              {
                propertyName: property.name || intl.formatMessage({
                  id: '1K3EXrzCdqY8du2zz77A',
                  defaultMessage: 'Vendor',
                  description: 'Fallback text for vendor when property name is not available',
                }),
              },
            )}
          />}
      </Group>
      {!(showReviewSummary && hasReviewSummaryContent) && isOfferRatingDisplayable(property.rating) && <CSSBreakpoint min="tablet">
        <OfferRating
          link
          inlineLabel
          variant="small"
          rating={property.rating}
        />
      </CSSBreakpoint>}
      {fetchingReviewSummary && showReviewSummary && <LoadingBox height={160} />}
      {reviewSummary?.summary && showReviewSummary && <OfferPageReviewAiSummary
        content={reviewSummary.summary}
        score={property.rating!.score}
        reviewsTotal={property.rating!.reviewsTotal}
      />}
      {(hasUpgradablePackages && !!loyaltyRoomUpgradeBenefitDetails?.availability) && <LoyaltyStayUpgradeBenefitPill offer={offer} hideViewOfferCta />}
      <CSSBreakpoint min="tablet">
        <FlashTierProgressionPanel offer={offer} packageViews={packageViews} checkoutDate={checkoutDate} />
      </CSSBreakpoint>
    </Group>
  )
}

export default memo(FlashHotelPropertyDetails)
